import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import CalorieRing from '../components/CalorieRing';
import MacroBar from '../components/MacroBar';
import { MOCK_DAILY_GOAL } from '../mock';
import { Camera, Flame, TrendingUp, Share2, Plus, Trash2, Loader2, Trophy, Target } from 'lucide-react';
import { toast } from 'sonner';
import { mealsToday, deleteMeal as apiDelete, paymentStatus, getStreak, getQuota } from '../lib/api';
import UpgradeNudge from '../components/UpgradeNudge';
import SubscriptionCard from '../components/SubscriptionCard';
import ReferralCard from '../components/ReferralCard';
import OnboardingModal from '../components/OnboardingModal';
import EnableNotifications from '../components/EnableNotifications';
import TrialCountdown from '../components/TrialCountdown';
import Seo from '../components/Seo';

const Dashboard = () => {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [meals, setMeals] = useState([]);
  const [mealsLoading, setMealsLoading] = useState(true);
  const [streak, setStreak] = useState({ current: 0, longest: 0 });
  const [quota, setQuota] = useState(null);
  const [onboardOpen, setOnboardOpen] = useState(false);

  useEffect(() => { if (!loading && !user) navigate('/'); }, [user, loading, navigate]);

  // Open onboarding for new users who haven't set weight goals yet
  useEffect(() => {
    if (user && !user.daily_target_calories && !user.current_weight_kg) {
      const t = setTimeout(() => setOnboardOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [user]);

  useEffect(() => {
    if (params.get('checkout') !== 'success') return;
    const sid = params.get('session_id');
    if (!sid) return;
    toast.info('Confirming your subscription…');
    let tries = 0;
    const poll = async () => {
      try {
        const s = await paymentStatus(sid);
        if (s.payment_status === 'paid') {
          toast.success('Subscription active! Welcome to CalorieSnap Pro 🎉');
          await refresh();
          window.history.replaceState(null, '', '/app');
          return;
        }
      } catch (e) { /* ignore */ }
      if (++tries < 10) setTimeout(poll, 1500);
    };
    poll();
  }, [params, refresh]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [m, s, q] = await Promise.all([mealsToday(), getStreak(), getQuota()]);
        setMeals(m); setStreak(s); setQuota(q);
      } catch (e) { /* silent */ }
      finally { setMealsLoading(false); }
    })();
  }, [user]);

  const totals = meals.reduce((acc, m) => ({
    calories: acc.calories + (m.calories || 0),
    protein: acc.protein + (m.protein || 0),
    carbs: acc.carbs + (m.carbs || 0),
    fat: acc.fat + (m.fat || 0),
  }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

  // Personalized goals (fall back to defaults if user hasn't set profile)
  const dailyCalGoal = user?.daily_target_calories || MOCK_DAILY_GOAL.calories;
  const w = user?.current_weight_kg;
  const goals = w ? {
    calories: dailyCalGoal,
    protein: Math.round(w * 1.6),
    fat: Math.round((dailyCalGoal * 0.25) / 9),
    carbs: Math.round((dailyCalGoal - (w * 1.6 * 4) - ((dailyCalGoal * 0.25))) / 4),
  } : { ...MOCK_DAILY_GOAL, calories: dailyCalGoal };

  const remaining = Math.max(goals.calories - totals.calories, 0);

  const handleDelete = async (id) => {
    try { await apiDelete(id); setMeals(meals.filter(m => m.meal_id !== id)); toast.success('Meal removed'); }
    catch (e) { toast.error('Failed to remove'); }
  };

  const shareDay = () => {
    navigator.clipboard?.writeText(`I hit ${totals.calories} kcal today with CalorieSnap — try it: ${window.location.origin}`);
    toast.success('Copied a shareable summary to your clipboard');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#fefdf9]">
      <Seo title="Your dashboard — CalorieSnap" description="See today's calories, macros and streak." canonical="https://www.thecaloriesnap.com/app" />
      <Navbar variant="app" />
      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-1">Today</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-emerald-950">Hi, {user.name?.split(' ')[0] || 'there'} 👋</h1>
            <p className="text-emerald-950/70 mt-1">{new Date().toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full border-emerald-950/10" onClick={() => setOnboardOpen(true)}><Target className="w-4 h-4 mr-2" />{user.daily_target_calories ? 'Edit goal' : 'Set goal'}</Button>
            <Button variant="outline" className="rounded-full border-emerald-950/10" onClick={shareDay}><Share2 className="w-4 h-4 mr-2" />Share day</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full" asChild><Link to="/app/snap"><Camera className="w-4 h-4 mr-2" />Snap a meal</Link></Button>
          </div>
        </div>

        {/* Trial countdown / trial-ended prompt */}
        {(user.plan === 'trial' || (quota && quota.on_trial)) && (
          <div className="mb-4"><TrialCountdown user={user} quota={quota} /></div>
        )}

        {/* Upgrade nudge (free users at 2+ scans today) */}
        {quota && <div className="mb-6"><UpgradeNudge quota={quota} /></div>}

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1 p-8 rounded-3xl border-emerald-950/5 flex flex-col items-center justify-center bg-white">
            <CalorieRing value={totals.calories} goal={goals.calories} size={220} label={`of ${goals.calories} kcal`} />
            <p className="mt-4 text-emerald-950/70 text-sm"><span className="font-semibold text-emerald-700">{remaining}</span> kcal remaining</p>
          </Card>

          <Card className="lg:col-span-2 p-8 rounded-3xl border-emerald-950/5 bg-white">
            <h3 className="font-display text-xl font-bold text-emerald-950 mb-6">Macros today</h3>
            <div className="space-y-5">
              <MacroBar label="Protein" value={totals.protein} goal={goals.protein} color="#059669" />
              <MacroBar label="Carbs" value={totals.carbs} goal={goals.carbs} color="#f97316" />
              <MacroBar label="Fat" value={totals.fat} goal={goals.fat} color="#eab308" />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-emerald-950/5">
              <div className="flex items-center gap-3">
                <div className={`relative w-11 h-11 rounded-xl grid place-items-center ${streak.current > 0 ? 'bg-orange-100 text-orange-600' : 'bg-emerald-50 text-emerald-400'}`}>
                  <Flame className="w-5 h-5" />
                  {streak.current >= 7 && <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-orange-500 text-white rounded-full px-1.5">{streak.current >= 30 ? '🏆' : streak.current >= 14 ? '🔥' : '★'}</span>}
                </div>
                <div><div className="font-display text-xl font-bold text-emerald-950">{streak.current} {streak.current === 1 ? 'day' : 'days'}</div><div className="text-xs text-emerald-950/60">current streak</div></div>
              </div>
              <div className="flex items-center gap-3"><div className="w-11 h-11 rounded-xl bg-yellow-100 text-yellow-700 grid place-items-center"><Trophy className="w-5 h-5" /></div><div><div className="font-display text-xl font-bold text-emerald-950">{streak.longest}</div><div className="text-xs text-emerald-950/60">longest streak</div></div></div>
              <div className="flex items-center gap-3"><div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center"><Camera className="w-5 h-5" /></div><div><div className="font-display text-xl font-bold text-emerald-950">{meals.length}</div><div className="text-xs text-emerald-950/60">meals today</div></div></div>
            </div>
          </Card>
        </div>

        {/* Subscription + Referral cards */}
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <SubscriptionCard user={user} onChange={refresh} />
          <ReferralCard />
        </div>

        {/* Enable 8pm reminder */}
        <div className="mt-4">
          <EnableNotifications />
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-emerald-950">Meals</h2>
            <Link to="/app/snap" className="text-emerald-700 font-medium text-sm flex items-center gap-1 hover:underline"><Plus className="w-4 h-4" /> Add meal</Link>
          </div>
          {mealsLoading ? (
            <Card className="p-12 text-center rounded-3xl border-emerald-950/5 bg-white"><Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" /></Card>
          ) : meals.length === 0 ? (
            <Card className="p-12 text-center rounded-3xl border-emerald-950/5 bg-white">
              <Camera className="w-10 h-10 mx-auto text-emerald-300 mb-3" />
              <p className="text-emerald-950/70">No meals yet today. Snap your first one!</p>
              <Button className="mt-4 bg-emerald-700 text-white rounded-full" asChild><Link to="/app/snap">Snap a meal</Link></Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {meals.map(m => (
                <Card key={m.meal_id} className="p-4 rounded-2xl border-emerald-950/5 bg-white flex gap-4 hover:shadow-md transition-shadow">
                  {m.image_base64 ? <img src={m.image_base64.startsWith('data:') || m.image_base64.startsWith('http') ? m.image_base64 : `data:image/jpeg;base64,${m.image_base64}`} alt={m.name} className="w-24 h-24 rounded-xl object-cover" /> : <div className="w-24 h-24 rounded-xl bg-emerald-50 grid place-items-center"><Camera className="w-6 h-6 text-emerald-300" /></div>}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-emerald-950/50">{new Date(m.created_at).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit' })}</p>
                        <h3 className="font-display font-bold text-emerald-950">{m.name}</h3>
                      </div>
                      <button onClick={() => handleDelete(m.meal_id)} className="text-emerald-950/30 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="flex gap-3 mt-2 text-xs">
                      <span className="font-semibold text-emerald-700">{m.calories} kcal</span>
                      <span className="text-emerald-950/60">P {m.protein}g</span>
                      <span className="text-emerald-950/60">C {m.carbs}g</span>
                      <span className="text-emerald-950/60">F {m.fat}g</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      <OnboardingModal open={onboardOpen} onClose={() => setOnboardOpen(false)} onSaved={refresh} />
    </div>
  );
};

export default Dashboard;
